import React, { useEffect, useState } from "react";
import { api, formatMoney } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { TrendingUp, Receipt, Repeat } from "lucide-react";

export default function Analytics() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.get("/analytics/summary").then(({ data }) => setSummary(data)).catch(() => {});
  }, []);

  const isMerchant = user?.role === "merchant";
  const data = summary?.series || [];

  return (
    <main data-testid="analytics-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="text-xs uppercase tracking-[0.3em] text-gold">Insights</div>
      <h1 className="font-display text-5xl tracking-tighter mt-3">Analytics</h1>
      <p className="text-secondary-fg mt-3">Last 30 days · all activity</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
        <Stat label={isMerchant ? "Revenue received" : "Top-ups received"} value={formatMoney(summary?.total_in ?? 0)} accent icon={<TrendingUp size={16} />} testid="analytics-in" />
        <Stat label={isMerchant ? "Transactions" : "Money sent"} value={isMerchant ? (summary?.total_count ?? 0).toString() : formatMoney(summary?.total_out ?? 0)} icon={<Receipt size={16} />} testid="analytics-count" />
        {isMerchant && (
          <Stat label="Active subscriptions" value={(summary?.active_subscriptions ?? 0).toString()} icon={<Repeat size={16} />} testid="analytics-subs" />
        )}
      </div>

      <section className="mt-14 lux-card p-8">
        <h2 className="font-display text-2xl tracking-tight mb-6">{isMerchant ? "Revenue trend" : "Activity trend"}</h2>
        {data.length === 0 ? (
          <div className="text-secondary-fg text-center py-16">No activity in the last 30 days</div>
        ) : (
          <div data-testid="analytics-chart" style={{ width: "100%", height: 320, minHeight: 320 }}>
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gold" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" stroke="#71717A" fontSize={11} />
                <YAxis stroke="#71717A" fontSize={11} />
                <Tooltip contentStyle={{ background: "#1A1A1C", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 4, color: "#fff" }} />
                <Area type="monotone" dataKey="in" stroke="#D4AF37" strokeWidth={2} fill="url(#gold)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>

      {isMerchant && summary?.top_links?.length > 0 && (
        <section className="mt-14">
          <h2 className="font-display text-2xl tracking-tight mb-6">Top payment links</h2>
          <div className="border hairline">
            <div className="grid grid-cols-12 px-6 py-3 border-b hairline text-[10px] uppercase tracking-[0.25em] text-muted-fg">
              <div className="col-span-7">Title</div>
              <div className="col-span-2">Times paid</div>
              <div className="col-span-3 text-right">Revenue</div>
            </div>
            {summary.top_links.map((l) => (
              <div key={l.link_id} className="grid grid-cols-12 px-6 py-5 border-b hairline last:border-b-0 hover:bg-surface transition-colors">
                <div className="col-span-7">
                  <div className="text-sm">{l.title}</div>
                  <div className="text-xs text-muted-fg font-mono mt-1">{l.link_id}</div>
                </div>
                <div className="col-span-2 self-center font-mono text-sm">{l.count}×</div>
                <div className="col-span-3 text-right font-display text-xl gold-text">{formatMoney(l.amount)}</div>
              </div>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}

function Stat({ label, value, icon, accent, testid }) {
  return (
    <div data-testid={testid} className="lux-card p-8">
      <div className="flex items-center justify-between text-xs uppercase tracking-[0.25em] text-muted-fg">
        <span>{label}</span>
        {icon && <span className="text-gold">{icon}</span>}
      </div>
      <div className={`font-display text-5xl mt-6 tracking-tighter ${accent ? "gold-text" : ""}`}>{value}</div>
    </div>
  );
}
